import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, Folder, Upload, Download, FileText, Image as ImageIcon, File,
  CheckCircle, AlertCircle, MessageSquare, Clock, User as UserIcon, Layers,
  Plus, Trash2, ChevronRight, History, Send, X, Loader2, ListChecks, ShieldCheck,
} from "lucide-react";

const STAGE_LABELS = {
  requirement_collection: "Requirements",
  design: "Designing",
  internal_review: "Internal QC",
  client_review: "Client Review",
  revisions: "Revisions",
  approval: "Approval",
  delivery: "Delivery",
  completed: "Completed",
};
const STAGES = Object.keys(STAGE_LABELS);

const STAGE_COLOR = {
  requirement_collection: "bg-amber-100 text-amber-700 border-amber-300",
  design: "bg-blue-100 text-blue-700 border-blue-300",
  internal_review: "bg-purple-100 text-purple-700 border-purple-300",
  client_review: "bg-orange-100 text-orange-700 border-orange-300",
  revisions: "bg-rose-100 text-rose-700 border-rose-300",
  approval: "bg-cyan-100 text-cyan-700 border-cyan-300",
  delivery: "bg-teal-100 text-teal-700 border-teal-300",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

export default function ProjectWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isDesigner = location.pathname.startsWith("/designer");
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("overview");

  const reload = () => api.get(`/projects/${id}`).then(r => setProject(r.data)).catch(() => navigate(isDesigner ? "/designer" : "/admin/orders"));
  useEffect(() => { reload(); }, [id]);

  if (!project) return <div className="p-8 text-center text-gray-500">Loading…</div>;

  const tabs = [
    ["overview", "Overview", Layers],
    ["requirement", "Requirements", ListChecks],
    ["files", "Files", Folder],
    ["approvals", "Approvals", CheckCircle],
    ["tasks", "Tasks", FileText],
    ["timeline", "Timeline", Clock],
  ];
  if (isAdmin) tabs.push(["qc", "Internal QC", ShieldCheck]);

  return (
    <div className="space-y-4" data-testid="project-workspace">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-600 hover:text-[#1a3c5e] inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <ProjectHeader project={project} onReload={reload} />

      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} data-testid={`pw-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 transition ${tab === k ? "text-[#1a3c5e] border-[#F7941D]" : "text-gray-500 border-transparent hover:text-[#1a3c5e]"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab project={project} onReload={reload} />}
      {tab === "requirement" && <RequirementTab projectId={id} readOnly={isDesigner} />}
      {tab === "files" && <FilesTab projectId={id} project={project} onReload={reload} />}
      {tab === "approvals" && <ApprovalsTab projectId={id} onReload={reload} />}
      {tab === "tasks" && <TasksTab projectId={id} />}
      {tab === "timeline" && <TimelineTab projectId={id} />}
      {tab === "qc" && isAdmin && <QcTab projectId={id} onReload={reload} />}
    </div>
  );
}

function ProjectHeader({ project, onReload }) {
  const [moving, setMoving] = useState(false);
  const moveStage = async (stage) => {
    setMoving(true);
    try {
      await api.post(`/projects/${project.id}/stage`, { stage });
      toast.success(`Moved to ${STAGE_LABELS[stage]}`);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setMoving(false); }
  };
  const stage = project.workflow_stage;
  const currIdx = STAGES.indexOf(stage);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="pw-header">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase text-gray-500 font-semibold">Project #{project.id.slice(0, 8).toUpperCase()}</div>
          <h2 className="text-2xl font-bold text-[#1a3c5e]">{project.package_title || "House Design Project"}</h2>
          <div className="text-sm text-gray-600">{project.customer?.name} · {project.customer?.phone} · {project.customer?.city}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Total</div>
          <div className="text-xl font-bold text-[#F7941D]">{inr(project.amount || 0)}</div>
          <div className="text-xs text-gray-500 mt-1">{project.status}</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs text-gray-500 mb-1">Progress: {project.workflow_progress || 0}%</div>
        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-[#F7941D] to-[#e0861a] transition-all" style={{ width: `${project.workflow_progress || 0}%` }} />
        </div>
        <div className="mt-3 grid grid-cols-4 md:grid-cols-8 gap-1 text-[10px]">
          {STAGES.map((s, i) => (
            <button key={s} disabled={moving} onClick={() => moveStage(s)} data-testid={`pw-stage-${s}`}
              className={`px-1.5 py-1.5 rounded text-center transition ${i <= currIdx ? STAGE_COLOR[s] + " border" : "bg-gray-50 text-gray-400 border border-gray-200"} hover:ring-1 hover:ring-[#F7941D]`}>
              <div className="font-bold">{i + 1}</div>
              <div className="truncate">{STAGE_LABELS[s]}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ project, onReload }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <KV label="Designer" value={project.designer_user?.name || "Not assigned"} />
      <KV label="Customer" value={project.customer?.name} />
      <KV label="City" value={project.customer?.city || "—"} />
      <KV label="Created" value={(project.created_at || "").slice(0, 10)} />
      <KV label="Files" value={project.files_count || 0} />
      <KV label="Tasks" value={project.tasks_count || 0} />
      <KV label="Pending Approvals" value={project.approvals_pending || 0} />
      <KV label="Revisions Used" value={`${project.revisions_used || 0} / ${project.revisions_limit ?? "∞"}`} />
      <KV label="Requirement %" value={`${project.requirement_progress || 0}%`} />
    </div>
  );
}
const KV = ({ label, value }) => (
  <div className="bg-white p-4 rounded-2xl border border-gray-200">
    <div className="text-xs uppercase text-gray-500 font-semibold">{label}</div>
    <div className="text-lg font-semibold text-[#1a3c5e] mt-1">{value}</div>
  </div>
);

function RequirementTab({ projectId, readOnly = false }) {
  const [data, setData] = useState({});
  const [schema, setSchema] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/requirement`).then(r => {
      setSchema(r.data.schema);
      const req = r.data.requirement;
      if (req) {
        setData(req.data || {});
        setProgress(req.progress || 0);
        setStatus(req.status);
      } else if (r.data.legacy) {
        setData(r.data.legacy);
        setStatus("legacy");
        setProgress(50);
      }
    });
  }, [projectId]);

  // autosave with debounce
  useEffect(() => {
    if (readOnly || !schema || status === "submitted" || status === "legacy") return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await api.put(`/projects/${projectId}/requirement`, { data });
        setProgress(r.data.progress);
      } catch {} finally { setSaving(false); }
    }, 1200);
    return () => clearTimeout(t);
  }, [data, schema, status, projectId]);

  const setField = (k, v) => setData(d => ({ ...d, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/requirement/submit`);
      toast.success("Requirements submitted");
      setStatus("submitted");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.missing) toast.error("Please fill required fields: " + detail.missing.join(", "));
      else toast.error(detail || "Failed");
    } finally { setSubmitting(false); }
  };

  if (!schema) return <div className="p-8 text-center text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="pw-requirement">
      {readOnly && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-2.5 text-sm font-semibold">
          These are the client's own requirements — view only. The client fills this in; it isn't something a designer edits.
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase font-semibold text-gray-500">Completion</div>
            <div className="text-2xl font-bold text-[#1a3c5e]">{progress}%</div>
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
            <span className={`text-xs px-2 py-0.5 rounded-full border ${status === "submitted" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-amber-50 border-amber-300 text-amber-700"}`}>{status}</span>
            {!readOnly && status !== "submitted" && status !== "legacy" && (
              <button onClick={submit} disabled={submitting || progress < 100} data-testid="pw-req-submit"
                className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-40 text-white rounded-xl text-sm font-bold inline-flex items-center gap-1">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Requirements
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-[#F7941D] to-[#e0861a]" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {schema.sections.map(sec => (
        <div key={sec.key} className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-lg font-bold text-[#1a3c5e] mb-3">{sec.label}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {sec.fields.map(f => (
              <DynField key={f.key} f={f} value={data[f.key]} onChange={v => setField(f.key, v)} disabled={readOnly || status === "submitted"} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DynField({ f, value, onChange, disabled }) {
  const baseClass = "w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#F7941D] disabled:bg-gray-50 disabled:text-gray-500";
  const lbl = (
    <div className="text-xs text-gray-600 mb-1">
      {f.label}{f.required && <span className="text-rose-500 ml-1">*</span>}
      {f.help && <span className="text-gray-400 ml-1 italic">— {f.help}</span>}
    </div>
  );
  if (f.type === "text") return <label data-testid={`pw-req-${f.key}`}>{lbl}<input disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass} /></label>;
  if (f.type === "number") return <label data-testid={`pw-req-${f.key}`}>{lbl}<input disabled={disabled} type="number" value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass} /></label>;
  if (f.type === "textarea") return <label data-testid={`pw-req-${f.key}`} className="md:col-span-2">{lbl}<textarea disabled={disabled} rows={3} value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass} /></label>;
  if (f.type === "boolean") return (
    <label data-testid={`pw-req-${f.key}`} className="flex items-center gap-2">
      <input disabled={disabled} type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      <span className="text-sm text-gray-700">{f.label}{f.required && <span className="text-rose-500 ml-1">*</span>}</span>
    </label>
  );
  if (f.type === "select") return (
    <label data-testid={`pw-req-${f.key}`}>{lbl}
      <select disabled={disabled} value={value || ""} onChange={e => onChange(e.target.value)} className={baseClass}>
        <option value="">— Select —</option>
        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
  if (f.type === "files") return (
    <label data-testid={`pw-req-${f.key}`} className="md:col-span-2">{lbl}
      <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-xl p-3">
        Upload files in the <strong>Files</strong> tab → folder "References".
      </div>
    </label>
  );
  return null;
}


function FilesTab({ projectId, project, onReload }) {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFolder, setActiveFolder] = useState("Floor Plans");
  const [uploading, setUploading] = useState(false);
  const [versionsOf, setVersionsOf] = useState(null);

  const load = () => api.get(`/projects/${projectId}/files?folder=${encodeURIComponent(activeFolder)}`).then(r => {
    setFiles(r.data.files); setFolders(r.data.folders); setCategories(r.data.categories);
  });
  useEffect(() => { load(); }, [projectId, activeFolder]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("folder", activeFolder);
    const dot = file.name.lastIndexOf(".");
    const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : "";
    const cat = ["dwg", "dxf", "rvt"].includes(ext) ? "dwg" : (activeFolder === "References" ? "reference" : (activeFolder === "Floor Plans" ? "floor_plan" : activeFolder === "Elevations" ? "elevation" : activeFolder === "3D Renders" ? "3d_render" : "other"));
    form.append("category", cat);
    try {
      await api.post(`/projects/${projectId}/files`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load(); onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addFolder = async () => {
    const name = window.prompt("Folder name?");
    if (!name?.trim()) return;
    try { await api.post(`/projects/${projectId}/folders`, { name: name.trim() }); load(); toast.success("Folder created"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const removeFile = async (fid) => {
    if (!window.confirm("Delete this file?")) return;
    try { await api.delete(`/project-files/${fid}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error("Failed"); }
  };

  const downloadUrl = (fid) => `${api.defaults.baseURL}/project-files/${fid}/download`;

  return (
    <div className="grid md:grid-cols-[200px_1fr] gap-4" data-testid="pw-files">
      <aside className="bg-white rounded-2xl border border-gray-200 p-2 h-fit">
        {folders.map(f => (
          <button key={f.id} data-testid={`pw-folder-${f.name}`} onClick={() => setActiveFolder(f.name)}
            className={`w-full text-left px-3 py-2 rounded-xl text-sm flex items-center gap-2 ${activeFolder === f.name ? "bg-[#FEF3E2] text-[#1a3c5e] font-bold" : "hover:bg-gray-50 text-gray-700"}`}>
            <Folder className="w-4 h-4" /> {f.name}
          </button>
        ))}
        <button onClick={addFolder} data-testid="pw-folder-add" className="w-full text-left px-3 py-2 rounded-xl text-xs text-[#F7941D] hover:bg-[#FEF3E2] inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New folder
        </button>
      </aside>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1a3c5e]">{activeFolder}</h3>
          <label className={`px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl text-sm font-bold cursor-pointer inline-flex items-center gap-1 ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="pw-file-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
            <input type="file" hidden onChange={upload} data-testid="pw-file-input" />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {files.length === 0 && <div className="col-span-full text-center py-10 text-gray-500 bg-white rounded-2xl border border-gray-200">No files in this folder yet.</div>}
          {files.map(f => {
            const isImg = (f.content_type || "").startsWith("image/");
            const isPdf = (f.content_type || "").includes("pdf");
            const isDwg = f.category === "dwg";
            const Icon = isImg ? ImageIcon : isPdf ? FileText : isDwg ? Layers : File;
            return (
              <div key={f.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid={`pw-file-${f.id}`}>
                <div className="aspect-video bg-gray-50 flex items-center justify-center">
                  {isImg ? <img src={downloadUrl(f.id)} alt={f.file_name} className="w-full h-full object-cover" />
                    : <Icon className="w-12 h-12 text-gray-400" />}
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold truncate" title={f.file_name}>{f.file_name}</div>
                  <div className="text-[10px] uppercase text-gray-500 mt-0.5">{f.category} · v{f.version} · {(f.size / 1024).toFixed(1)} KB</div>
                  <div className="mt-2 flex items-center gap-1">
                    <a href={downloadUrl(f.id)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-gray-100 rounded" title="Open"><Download className="w-4 h-4 text-[#1a3c5e]" /></a>
                    <button onClick={() => setVersionsOf(f)} className="p-1.5 hover:bg-gray-100 rounded" title="Versions" data-testid={`pw-file-versions-${f.id}`}><History className="w-4 h-4 text-gray-500" /></button>
                    <button onClick={() => removeFile(f.id)} className="p-1.5 hover:bg-rose-50 rounded ml-auto" title="Delete" data-testid={`pw-file-delete-${f.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {versionsOf && <VersionsModal file={versionsOf} onClose={() => setVersionsOf(null)} downloadUrl={downloadUrl} />}
      </div>
    </div>
  );
}

function VersionsModal({ file, onClose, downloadUrl }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get(`/project-files/${file.id}/versions`).then(r => setList(r.data));
  }, [file.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pw-versions-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e]">Versions of {file.file_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {list.map(v => (
            <div key={v.id} className="px-4 py-2 border-b border-gray-100 flex items-center gap-2 text-sm">
              <span className="font-mono">v{v.version}</span>
              <span className="text-xs text-gray-500">{(v.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <span className="text-xs text-gray-500">{(v.size / 1024).toFixed(1)} KB</span>
              {v.is_latest && <span className="text-[10px] uppercase text-emerald-600 font-bold">latest</span>}
              <a href={downloadUrl(v.id)} target="_blank" rel="noreferrer" className="ml-auto text-[#F7941D] hover:underline text-xs">Download</a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function ApprovalsTab({ projectId, onReload }) {
  const [list, setList] = useState([]);
  const [files, setFiles] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    api.get(`/projects/${projectId}/approvals`).then(r => setList(r.data));
    api.get(`/projects/${projectId}/files`).then(r => setFiles(r.data.files));
  };
  useEffect(() => { load(); }, [projectId]);

  const create = async (payload) => {
    try {
      await api.post(`/projects/${projectId}/approvals`, payload);
      toast.success("Approval requested");
      setShowCreate(false);
      load(); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const decide = async (aid, decision) => {
    const notes = decision === "revision" ? window.prompt("Revision notes?") : window.prompt("Notes (optional)") || "";
    if (decision === "revision" && !notes?.trim()) { toast.error("Revision notes required"); return; }
    try {
      await api.post(`/projects/${projectId}/approvals/${aid}/decide`, { decision, notes });
      toast.success(`Marked ${decision}`);
      load(); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="pw-approvals">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#1a3c5e]">Drawing Approvals</h3>
        <button data-testid="pw-approval-new" onClick={() => setShowCreate(true)} className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl text-sm font-bold inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Request Approval
        </button>
      </div>
      {list.length === 0 && <div className="text-center py-10 text-gray-500 bg-white rounded-2xl border border-gray-200">No approvals yet.</div>}
      {list.map(a => (
        <div key={a.id} className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`pw-approval-${a.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-[#1a3c5e]">{a.title}</div>
              {a.description && <div className="text-sm text-gray-600 mt-1">{a.description}</div>}
              {a.file && <div className="text-xs text-gray-500 mt-1">File: {a.file.file_name} (v{a.file.version})</div>}
              <div className="text-xs text-gray-400 mt-1">Requested {(a.requested_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
            <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${
              a.status === "approved" ? "bg-emerald-100 text-emerald-700 border-emerald-300"
              : a.status === "revision_requested" ? "bg-rose-100 text-rose-700 border-rose-300"
              : a.status === "cancelled" ? "bg-gray-100 text-gray-500 border-gray-300"
              : "bg-amber-100 text-amber-700 border-amber-300"}`}>{a.status}</span>
          </div>
          {a.decision_notes && <div className="mt-2 bg-gray-50 p-2 rounded text-xs text-gray-700"><strong>Notes:</strong> {a.decision_notes}</div>}
          {a.status === "pending" && (
            <div className="mt-3 flex gap-2">
              <button onClick={() => decide(a.id, "approved")} data-testid={`pw-approve-${a.id}`} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Approve</button>
              <button onClick={() => decide(a.id, "revision")} data-testid={`pw-revise-${a.id}`} className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Request Revision</button>
            </div>
          )}
        </div>
      ))}
      {showCreate && <CreateApprovalModal files={files} onClose={() => setShowCreate(false)} onSubmit={create} />}
    </div>
  );
}

function CreateApprovalModal({ files, onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fileId, setFileId] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pw-approval-modal">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e]">Request Drawing Approval</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <label><div className="text-xs text-gray-600 mb-1">Title *</div>
            <input data-testid="pw-approval-title" value={title} onChange={e => setTitle(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" placeholder="e.g., Final Floor Plan v1" /></label>
          <label><div className="text-xs text-gray-600 mb-1">Linked File (optional)</div>
            <select data-testid="pw-approval-file" value={fileId} onChange={e => setFileId(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
              <option value="">— None —</option>
              {files.map(f => <option key={f.id} value={f.id}>{f.folder} / {f.file_name} (v{f.version})</option>)}
            </select></label>
          <label><div className="text-xs text-gray-600 mb-1">Description</div>
            <textarea data-testid="pw-approval-desc" rows={3} value={description} onChange={e => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" /></label>
        </div>
        <div className="border-t border-gray-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl hover:bg-gray-100">Cancel</button>
          <button onClick={() => onSubmit({ title, description, file_id: fileId || null })} disabled={!title.trim()} data-testid="pw-approval-submit"
            className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white text-sm font-bold rounded-xl">Send for Approval</button>
        </div>
      </div>
    </div>
  );
}


function TasksTab({ projectId }) {
  const [list, setList] = useState([]);
  const load = () => api.get(`/projects/${projectId}/tasks`).then(r => setList(r.data));
  useEffect(() => { load(); }, [projectId]);

  const addTask = async () => {
    const title = window.prompt("Task title?");
    if (!title?.trim()) return;
    await api.post(`/projects/${projectId}/tasks`, { title, priority: "medium" });
    load();
  };
  const setStatus = async (tid, status) => {
    try { await api.put(`/designer/tasks/${tid}`, { status }); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (tid) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/designer/tasks/${tid}`); load();
  };

  const cols = ["todo", "in_progress", "blocked", "done"];
  const colLabel = { todo: "To Do", in_progress: "In Progress", blocked: "Blocked", done: "Done" };

  return (
    <div className="space-y-3" data-testid="pw-tasks">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-[#1a3c5e]">Designer Task Board</h3>
        <button onClick={addTask} data-testid="pw-task-add" className="px-3 py-1.5 bg-[#F7941D] text-white rounded-lg text-xs font-bold inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add Task</button>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        {cols.map(c => (
          <div key={c} className="bg-gray-50 border border-gray-200 rounded-2xl p-3 min-h-[200px]">
            <div className="text-xs font-bold uppercase text-[#1a3c5e] mb-2">{colLabel[c]} ({list.filter(t => t.status === c).length})</div>
            <div className="space-y-2">
              {list.filter(t => t.status === c).map(t => (
                <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-2.5" data-testid={`pw-task-${t.id}`}>
                  <div className="text-sm font-semibold">{t.title}</div>
                  {t.description && <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>}
                  <div className="text-[10px] uppercase mt-1">
                    <span className={`px-1.5 py-0.5 rounded ${t.priority === "high" ? "bg-rose-100 text-rose-700" : t.priority === "medium" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{t.priority}</span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    <select value={t.status} onChange={e => setStatus(t.id, e.target.value)} className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5">
                      {cols.map(s => <option key={s} value={s}>{colLabel[s]}</option>)}
                    </select>
                    <button onClick={() => remove(t.id)} className="ml-auto text-rose-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function TimelineTab({ projectId }) {
  const [data, setData] = useState({ events: [], stage_history: [] });
  useEffect(() => { api.get(`/projects/${projectId}/timeline`).then(r => setData(r.data)); }, [projectId]);
  const all = [
    ...data.stage_history.map(h => ({ at: h.at, type: "stage", label: `Stage: ${h.from || "—"} → ${h.to}`, by: h.by, note: h.note })),
    ...data.events.map(e => ({ at: e.created_at, type: e.event_type, label: (e.event_type || "").replace(/_/g, " ").replace(/\./g, " · "), by: e.actor_id, note: JSON.stringify(e.details || {}).slice(0, 120) })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return (
    <div className="space-y-2" data-testid="pw-timeline">
      {all.length === 0 && <div className="text-center py-10 text-gray-500 bg-white rounded-2xl border border-gray-200">No timeline events yet.</div>}
      {all.map((e, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-200 p-3 text-sm" data-testid={`pw-timeline-row-${i}`}>
          <div className="flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-[#F7941D]" />
            <span className="font-semibold text-[#1a3c5e]">{e.label}</span>
            <span className="ml-auto text-[10px] text-gray-400 font-mono">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
          {e.note && <div className="text-xs text-gray-500 mt-1 ml-6 truncate">{e.note}</div>}
        </div>
      ))}
    </div>
  );
}


const QC_ITEM_LABELS = {
  dimensions_checked: "Dimensions checked against the site plot",
  matches_requirements: "Design matches the client's stated requirements",
  no_missing_sheets: "No missing sheets (floor plan, elevation, 3D, working drawing)",
};

function QcTab({ projectId, onReload }) {
  const [qc, setQc] = useState(null);
  const [passing, setPassing] = useState(false);

  const load = () => api.get(`/projects/${projectId}/qc`).then(r => setQc(r.data));
  useEffect(() => { load(); }, [projectId]);

  const toggle = async (key, value) => {
    setQc(q => ({ ...q, items: { ...q.items, [key]: value }, passed: value ? q.passed : false }));
    try { await api.put(`/projects/${projectId}/qc`, { items: { [key]: value } }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); load(); }
  };

  const pass = async () => {
    setPassing(true);
    try {
      await api.post(`/projects/${projectId}/qc/pass`);
      toast.success("Internal QC passed — moved to Client Review");
      load(); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPassing(false); }
  };

  if (!qc) return <div className="p-8 text-center text-gray-500">Loading…</div>;

  const allChecked = Object.keys(QC_ITEM_LABELS).every(k => qc.items[k]);

  return (
    <div className="space-y-4" data-testid="pw-qc">
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#1a3c5e]">Internal QC Checklist</h3>
            <p className="text-xs text-gray-500 mt-0.5">A designer can't request client approval on this project until this passes.</p>
          </div>
          <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border font-bold ${qc.passed ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-amber-100 text-amber-700 border-amber-300"}`}>
            {qc.passed ? "Passed" : "Pending"}
          </span>
        </div>

        <div className="space-y-2.5">
          {Object.entries(QC_ITEM_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-gray-50" data-testid={`qc-item-${key}`}>
              <input type="checkbox" checked={!!qc.items[key]} onChange={e => toggle(key, e.target.checked)}
                className="w-4 h-4 accent-[#F7941D]" disabled={qc.passed} />
              <span className="text-sm text-[#1a3c5e]">{label}</span>
            </label>
          ))}
        </div>

        {!qc.passed && (
          <button onClick={pass} disabled={!allChecked || passing} data-testid="qc-pass-btn"
            className="mt-4 px-5 py-2.5 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-40 text-white rounded-xl text-sm font-bold inline-flex items-center gap-1.5">
            {passing && <Loader2 className="w-4 h-4 animate-spin" />} Mark QC Passed → Client Review
          </button>
        )}
        {qc.passed && (
          <div className="mt-4 text-xs text-gray-500">Passed {(qc.passed_at || "").slice(0, 16).replace("T", " ")}</div>
        )}
      </div>
    </div>
  );
}
